declare let __BUILD_TIMESTAMP__: string;
declare let __BUILD_COMMITISH__: string;

import "./libs/typescript/event-emitter-shim";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { enableProdMode, Injector } from "@angular/core";

import { platformBrowserDynamic } from "@angular/platform-browser-dynamic";

import {
  bootstrapWorkerProvider,
  WORKER_FETCH_RESPONSE,
  WORKER_REF,
} from "shared/provider-webworker";

import { environment } from "./environments/environment";
import { bootstrap_theme } from "./theme";
import { bootstrap_css_install_vars } from "./cssvars";
import {
  OrdersTableDefinitionFactory,
  BaseTableDefinitionFactory,
  ProfilePrimaryActionService,
  ProfilePromiseActionService,
} from "shared/cubiq-services";

import { AppModule } from "./app/app.module";

let closeAfterTokenRegExp = /login=closeAfterToken/;
const closeAfterToken: boolean =
  module.hot &&
  module.hot.data != null &&
  typeof module.hot.data === "object" &&
  module.hot.data != null &&
  typeof module.hot.data === "object"
    ? module.hot.data.closeAfterToken
    : closeAfterTokenRegExp.exec(window.location.hash) != null;

let sessionTimestampRegExp = /sessionTimestamp=([0-9]+)/;
let sessionTimestampMatch = sessionTimestampRegExp.exec(window.location.hash);
const sessionTimestamp: string =
  module.hot &&
  module.hot.data != null &&
  typeof module.hot.data === "object" &&
  module.hot.data != null &&
  typeof module.hot.data === "object"
    ? module.hot.data.sessionTimestamp
    : sessionTimestampMatch != null
    ? sessionTimestampMatch[1]
    : `${new Date().valueOf()}`;

export const socketWorkerUri = `cubiq-broker-socket-worker.${__BUILD_COMMITISH__}.bundle.js?v=${__BUILD_TIMESTAMP__}#socket-worker;sessionTimestamp=${sessionTimestamp}`;

if (module.hot) {
  logger.out.info(`HMR initial sessionTimestamp: ${sessionTimestamp}`);
}

import {
  SESSION_TIMESTAMP,
  SESSION_IS_ROOT,
  CubiqBrokerClient,
  CUBIQ_BROKER,
  CUBIQ_BROKER_PORT,
  CUBIQ_LOGIN_CLOSE_AFTER_TOKEN,
} from "shared/broker-client-services";

import { Serializer } from "shared/provider-webworker";
import { CubiqSerializer } from "shared/cubiq-model-multi-asset";
import { loggerBootstrap } from "./logger";
import { load_tzdb } from "./tzdb";
import { cacheBust } from "./cache";

interface IdleDetector {
  addEventListener(
    type: "change",
    listener: (
      this: IdleDetector,
      ev: { userState: "active" | "idle"; screenState: "locked" | "unlocked" },
    ) => unknown,
    options?: boolean | AddEventListenerOptions,
  ): void;
  start(options: { threshold: number; signal: AbortSignal }): Promise<void>;
  screenState: "locked" | "unlocked";
  userState: "active" | "idle";
}

declare const IdleDetector: {
  new (): IdleDetector;
  requestPermission(): Promise<"granted" | "denied">;
};

window.addEventListener("error", (event: ErrorEvent) => {
  if (event == null) {
    logger.out.error("null uncaught Error");
    return;
  }

  if (event instanceof ErrorEvent) {
    if (event.message == "ResizeObserver loop completed with undelivered notifications.") {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    logger.out.error(`${event.filename}#${event.lineno}:${event.colno} ${event.message}`);
  }

  if (event.error != null) logger.out.error(event.error);
});

let bootstrap;
let broker = null;
let broker_port = null;

if (!environment.debug) {
  logger.out.info("Running in production mode");
  enableProdMode();
  cacheBust();
}

bootstrap = async (injector: Injector) => {
  const socketWorker = injector.get(WORKER_REF(socketWorkerUri));
  const interWorkerMessageChannel = new MessageChannel();

  let response = injector.get(WORKER_FETCH_RESPONSE(socketWorkerUri));

  if (response instanceof Error) {
    logger.out.info(`bootstrap(): HTTP error loading socket worker, error = %O`, response);
  } else {
    response = response?.clone();

    if (response?.ok === true) {
      logger.out.info(
        `bootstrap(): HTTP status loading socket worker, status = ${response.status}`,
      );
    } else {
      logger.out.error(
        `bootstrap(): HTTP error loading socket worker, status = ${response?.status}`,
      );
    }
  }

  broker = new CubiqBrokerClient(sessionTimestamp);
  broker.setTargetPort(socketWorker.port, interWorkerMessageChannel.port1);
  broker_port = interWorkerMessageChannel.port2;

  return platformBrowserDynamic([
    { provide: CUBIQ_BROKER, useValue: broker },
    { provide: CUBIQ_BROKER_PORT, useValue: broker_port },
    { provide: SESSION_TIMESTAMP, useValue: sessionTimestamp },
    { provide: SESSION_IS_ROOT, useValue: !closeAfterToken },
    { provide: CUBIQ_LOGIN_CLOSE_AFTER_TOKEN, useValue: closeAfterToken },
    {
      provide: BaseTableDefinitionFactory,
      useValue: OrdersTableDefinitionFactory,
    },
    {
      provide: ProfilePromiseActionService,
      useValue: ProfilePrimaryActionService,
    },
  ])
    .bootstrapModule(AppModule)
    .then(async () => {
      bootstrap_css_install_vars("oms");

      await load_tzdb();

      if (module.hot) {
        const disposeFn = (data) => {
          logger.out.info(`HMR storing sessionTimestamp: ${sessionTimestamp}`);
          data.sessionTimestamp = sessionTimestamp;
        };

        if (module.hot.data == null) {
          module.hot.data = {
            sessionTimestamp: sessionTimestamp,
          };
        } else {
          module.hot.data.sessionTimestamp = sessionTimestamp;
        }
        module.hot.accept((err, ...acceptErrArgs) => {
          let { moduleId, module } = (acceptErrArgs as any[])[0];
          logger.out.info(`HMR accepting reload for moduleId: ${moduleId} after error;`);
          logger.out.info(err);

          module.hot.accept();
          module.hot.data.sessionTimestamp = sessionTimestamp;
          module.hot.dispose(disposeFn);
        });

        module.hot.dispose(disposeFn);
        bootstrap_theme(null, "hmr", "assets/hmr.css");
      } else {
        bootstrap_theme(null, "dark", `theme-default.${__BUILD_COMMITISH__}.css`);
      }
    });
};

bootstrapWorkerProvider(
  [socketWorkerUri],
  [
    {
      provide: Serializer,
      useFactory: () => {
        return new CubiqSerializer();
      },
      deps: [],
    },
  ],
)
  .then(loggerBootstrap)
  .then((injector) => bootstrap(injector));
